import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { Warning } from '@mui/icons-material';
import { fetchMFAPreference } from 'aws-amplify/auth';
import { AppButton, AppTextField } from '../design-system';
import {
  isStepUpMfaRequired,
  stepUpAuthService,
  type StepUpAuthError,
} from '../../services/StepUpAuthService';
import type { StepUpAction } from '@cnapse/shared/schemas.js';

export type SensitiveActionType = StepUpAction;

export interface SensitiveActionTarget {
  id: string;
  email: string;
  displayName: string;
}

export interface SensitiveActionContext {
  newRoleLabel?: string;
}

interface SensitiveActionDialogProps {
  open: boolean;
  action: SensitiveActionType | null;
  target: SensitiveActionTarget | null;
  context?: SensitiveActionContext;
  onClose: () => void;
  onConfirm: (stepUpToken: string) => Promise<void>;
}

type ActionCopy = {
  title: string;
  warning: string;
  confirmLabel: string;
  confirmColor: 'warning' | 'error';
};

function getActionCopy(
  action: SensitiveActionType,
  context?: SensitiveActionContext,
): ActionCopy {
  switch (action) {
    case 'reset-password':
      return {
        title: 'Reset user password',
        warning:
          'A password reset code will be emailed to the user. They must complete the forgot-password flow to sign in again. Their current password may stop working once they set a new one.',
        confirmLabel: 'Send reset code',
        confirmColor: 'warning',
      };
    case 'resend-invitation':
      return {
        title: 'Resend invitation',
        warning:
          'A new temporary password will be generated and emailed to the user. They will be required to change it on first login. Any existing session will no longer work.',
        confirmLabel: 'Resend invitation',
        confirmColor: 'warning',
      };
    case 'enable-user':
      return {
        title: 'Enable user account',
        warning:
          'This user will be able to sign in again and access the platform according to their role.',
        confirmLabel: 'Enable user',
        confirmColor: 'warning',
      };
    case 'disable-user':
      return {
        title: 'Disable user account',
        warning:
          'This user will be signed out and unable to access the platform until re-enabled. Their data will be retained.',
        confirmLabel: 'Disable user',
        confirmColor: 'warning',
      };
    case 'delete-user':
      return {
        title: 'Delete user',
        warning:
          'This user will be removed from the directory and will lose access permanently. This action cannot be undone.',
        confirmLabel: 'Delete user',
        confirmColor: 'error',
      };
    case 'change-role':
      return {
        title: 'Change user role',
        warning: context?.newRoleLabel
          ? `This user will be assigned the ${context.newRoleLabel} role. Their permissions may change immediately.`
          : "This user's role will change. Their permissions may change immediately.",
        confirmLabel: 'Change role',
        confirmColor: 'warning',
      };
    case 'resend-tenant-invitation':
      return {
        title: 'Resend tenant admin invitation',
        warning:
          'A new temporary password will be generated and emailed to the tenant administrator. They will be required to change it on first login.',
        confirmLabel: 'Resend invitation',
        confirmColor: 'warning',
      };
    case 'delete-tenant':
      return {
        title: 'Delete tenant',
        warning:
          'This will deactivate all tenant users in Cognito, mark the tenant as deleted, and prevent access to tenant resources. This cannot be undone from the UI.',
        confirmLabel: 'Delete tenant',
        confirmColor: 'error',
      };
  }
}

export const SensitiveActionDialog: React.FC<SensitiveActionDialogProps> = ({
  open,
  action,
  target,
  context,
  onClose,
  onConfirm,
}) => {
  const [emailConfirm, setEmailConfirm] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [showTotpField, setShowTotpField] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const copy = action ? getActionCopy(action, context) : null;
  const loginPageUrl =
    typeof window !== 'undefined' ? `${window.location.origin}/login` : '/login';
  const emailMatches =
    target != null &&
    emailConfirm.trim().toLowerCase() === target.email.trim().toLowerCase();

  const resetForm = useCallback(() => {
    setEmailConfirm('');
    setPassword('');
    setTotpCode('');
    setShowTotpField(false);
    setError(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!open) {
      resetForm();
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const preference = await fetchMFAPreference();
        if (cancelled) return;
        const enabled = (preference.enabled ?? []).includes('TOTP');
        setMfaEnabled(enabled);
        setShowTotpField(enabled);
      } catch {
        if (!cancelled) {
          setMfaEnabled(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, resetForm]);

  const handleClose = () => {
    if (loading) return;
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!action || !target || !emailMatches || !password.trim()) {
      return;
    }

    if ((mfaEnabled || showTotpField) && totpCode.trim().length !== 6) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await stepUpAuthService.verifyStepUp({
        password,
        totpCode: totpCode.trim() || undefined,
        action,
        targetId: target.id,
      });
      await onConfirm(result.stepUpToken);
      handleClose();
    } catch (err) {
      const stepUpError = err as StepUpAuthError;
      if (isStepUpMfaRequired(stepUpError)) {
        setShowTotpField(true);
        setError('Enter the 6-digit code from your authenticator app to confirm this action.');
      } else {
        setError(
          stepUpError.message ||
            'Could not complete this action. Check your credentials and try again.',
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const canSubmit =
    Boolean(copy && target && emailMatches && password.trim()) &&
    (!showTotpField || totpCode.trim().length === 6);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Warning color={copy?.confirmColor === 'error' ? 'error' : 'warning'} />
          <Typography variant="h6" component="span">
            {copy?.title ?? 'Confirm sensitive action'}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {target && (
            <Typography variant="body1">
              You are about to perform this action for{' '}
              <strong>{target.displayName}</strong> ({target.email}).
            </Typography>
          )}

          {action === 'reset-password' && (
            <Alert severity="info" variant="outlined">
              <Typography variant="body2" component="div">
                <strong>Try self-service first:</strong> Ask the user to open the{' '}
                <Link href={loginPageUrl} target="_blank" rel="noopener noreferrer">
                  login page
                </Link>
                , click <strong>Forgot your password?</strong>, and follow the instructions
                emailed to them. Only proceed with an admin reset if they cannot access their
                email or self-service did not work.
              </Typography>
            </Alert>
          )}

          {copy && (
            <Alert severity={copy.confirmColor} variant="outlined">
              {copy.warning}
            </Alert>
          )}

          <AppTextField
            label="Confirm user email"
            placeholder={target?.email ?? ''}
            value={emailConfirm}
            onChange={(e) => setEmailConfirm(e.target.value)}
            fullWidth
            autoComplete="off"
            helperText="Type the user's email address to confirm"
            error={emailConfirm.length > 0 && !emailMatches}
          />

          <AppTextField
            label="Your password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            autoComplete="current-password"
            helperText="Re-enter your password to authorize this action"
          />

          {showTotpField && (
            <AppTextField
              label="Authenticator code"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              fullWidth
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
              helperText={
                mfaEnabled
                  ? 'Enter the 6-digit TOTP code from your authenticator app'
                  : 'MFA verification required'
              }
            />
          )}

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <AppButton variant="outlined" onClick={handleClose} disabled={loading}>
          Cancel
        </AppButton>
        <AppButton
          variant="contained"
          color={copy?.confirmColor ?? 'warning'}
          onClick={() => void handleSubmit()}
          disabled={!canSubmit || loading}
        >
          {loading ? 'Verifying…' : copy?.confirmLabel ?? 'Confirm'}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default SensitiveActionDialog;
